import React, { useMemo, useRef } from "react";
import Table from "react-bootstrap/Table";
import { Bar } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

// Image assets
const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

const num = (v) => Number(v) || 0;
const fmt = (v) => num(v).toLocaleString("en-US");

const AeroTable10 = ({ data = [] }) => {
  const pdfRef = useRef(null);

  const years = useMemo(
    () => ["2018", "2019", "2020", "2021", "2022", "2023", "2024"],
    [],
  );

  // Build table headers + chart labels
  const genders = useMemo(() => ["Female", "Male"], []);
  const chartLabels = useMemo(
    () => years.flatMap((y) => genders.map((g) => `${g} (${y})`)),
    [years, genders],
  );

  // Clean up / normalize
  const cleaned = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data;
  }, [data]);

  // Summary metrics
  const computed = useMemo(() => {
    if (cleaned.length === 0) return null;

    const rowSums = cleaned.map((row) => {
      let femaleTotal = 0;
      let maleTotal = 0;

      years.forEach((y) => {
        femaleTotal += num(row[`Female-${y}`]);
        maleTotal += num(row[`Male-${y}`]);
      });

      return {
        ageGroup: row["Age Group"],
        femaleTotal,
        maleTotal,
        total: femaleTotal + maleTotal,
      };
    });

    const topFemale = rowSums.reduce(
      (a, b) => (b.femaleTotal > a.femaleTotal ? b : a),
      rowSums[0],
    );
    const topMale = rowSums.reduce(
      (a, b) => (b.maleTotal > a.maleTotal ? b : a),
      rowSums[0],
    );
    const topOverall = rowSums.reduce(
      (a, b) => (b.total > a.total ? b : a),
      rowSums[0],
    );

    // 2024 leaders
    const y2024 = rowSums.map((row, idx) => ({
      ageGroup: cleaned[idx]["Age Group"],
      female2024: num(cleaned[idx]["Female-2024"]),
      male2024: num(cleaned[idx]["Male-2024"]),
      total2024:
        num(cleaned[idx]["Female-2024"]) + num(cleaned[idx]["Male-2024"]),
    }));

    const top2024 = y2024.reduce(
      (a, b) => (b.total2024 > a.total2024 ? b : a),
      y2024[0],
    );

    return {
      topFemale,
      topMale,
      topOverall,
      top2024,
    };
  }, [cleaned, years]);

  const predefinedColors = [
    "#FFA1B5",
    "#86C7F3",
    "#FFE29A",
    "#93D9D9",
    "#C2A3FF",
    "#FFC58C",
    "#B9D2AA",
    "#6794D1",
    "rgba(1,76,178,0.6)",
  ];

  // Chart Data: stacked bars across (Female 2018, Male 2018, ...)
  const chartData = useMemo(() => {
    return {
      labels: chartLabels,
      datasets: cleaned.map((row, idx) => ({
        label: row["Age Group"],
        data: years.flatMap((y) => [
          num(row[`Female-${y}`]),
          num(row[`Male-${y}`]),
        ]),
        backgroundColor: predefinedColors[idx % predefinedColors.length],
        borderColor: predefinedColors[idx % predefinedColors.length],
        borderWidth: 1,
        borderRadius: 4,
      })),
    };
  }, [cleaned, years, chartLabels]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: "📊 Male vs Female Counts by Age Group (2018–2024)",
        font: { size: 16, weight: "bold" },
        padding: { top: 8, bottom: 8 },
      },
      legend: {
        position: "top",
        labels: { font: { size: 11 }, boxWidth: 12, padding: 10 },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: "Gender / Year" },
        ticks: {
          maxRotation: window.innerWidth < 768 ? 90 : 45,
          font: { size: window.innerWidth < 768 ? 10 : 12 },
        },
        grid: { display: false },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: { display: true, text: "Count" },
        ticks: { callback: (v) => fmt(v) },
        grid: { display: true },
      },
    },
  };

   const downloadPDF = () => {
     const input = pdfRef.current;
     html2canvas(input, {
       scale: 2,
       useCORS: true,
       backgroundColor: "#ffffff",
       scrollY: -window.scrollY,
     }).then((canvas) => {
       const imgData = canvas.toDataURL("image/png");
       const pdf = new jsPDF("p", "mm", "a4");
       const pageWidth = pdf.internal.pageSize.getWidth();
       const pageHeight = pdf.internal.pageSize.getHeight();
       const imgWidth = pageWidth - 10;
       const imgHeight = (canvas.height * imgWidth) / canvas.width;

       let heightLeft = imgHeight;
       let position = 0;
       let pageNumber = 1;

       const addHeader = () => {
         const logoWidth = 20; // ideal header width
         const logoHeight = 20; // adjust ratio if needed

         pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
         pdf.setFontSize(26);
         pdf.text("UTahData.org", 95, 22);
         pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
         pdf.line(10, 35, 200, 35);
       };

       const addFooter = (pageNumber) => {
         pdf.setFontSize(9);
         const footerY = pageHeight - 10;
         pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
         pdf.text(
           `Downloaded on: ${new Date().toLocaleDateString()}`,
           10,
           footerY,
         );
         pdf.text(
           `Page ${pageNumber}`,
           pageWidth - 10,
           footerY,
           null,
           null,
           "right",
         );
       };

       // First page
       addHeader();
       pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;

       // Add more pages if needed
       while (heightLeft > 0) {
         pdf.addPage();
         pageNumber++;
         addHeader();
         position = heightLeft - imgHeight;
         pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
         heightLeft -= pageHeight - 32;
         addFooter(pageNumber);
       }

       // Final footer
       // ===== FINAL FOOTER SAFE AREA =====

       const footerTopY = pageHeight - 40; // start footer 40mm from bottom
       const footerContentY = pageHeight - 28; // logos row
       const footerBottomTextY = pageHeight - 10; // bottom text row

       // Top Divider Line
       pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

       // UtahData logo
       const utahWidth = 18;
       const utahHeight = 18;
       pdf.addImage(
         utahData,
         "PNG",
         20,
         footerContentY - 8,
         utahWidth,
         utahHeight,
       );

       // Built by
       pdf.setFontSize(10);
       pdf.text("Built by", 45, footerContentY);

       // BODEX logo
       const bodexWidth = 36;
       const bodexHeight = bodexWidth * 0.3;
       pdf.addImage(
         bodex,
         "PNG",
         60,
         footerContentY - 6,
         bodexWidth,
         bodexHeight,
       );

       // Using text
       pdf.text("using", 102, footerContentY);

       // KPI logo
       pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

       // Version
       pdf.text("20250916.v3.198", 140, footerContentY);

       // Bottom Info Line
       pdf.setFontSize(8);
       pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         pageWidth - 10,
         footerBottomTextY,
         null,
         null,
         "right",
       );

       pdf.save("male-female-by-age-group-2018-2024.pdf");
     });
   };

  if (!data || data.length === 0) return <p>No data available</p>;

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white rounded-xl shadow-md"
        style={{
          background:
            "linear-gradient(135deg, rgba(239,246,255,1) 0%, rgba(255,255,255,1) 45%, rgba(253,242,248,1) 100%)",
        }}
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Highest Total Male & Female Population (Age-wise, 2018–2024)
        </h2>

        {/* Summary Cards */}
        {computed && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
            <div className="p-4 bg-pink-100 rounded-xl shadow">
              <p className="text-sm text-gray-600 font-medium">
                Top Female (Total)
              </p>
              <p className="text-lg font-bold text-pink-800">
                {computed.topFemale.ageGroup}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {fmt(computed.topFemale.femaleTotal)}
              </p>
            </div>

            <div className="p-4 bg-blue-100 rounded-xl shadow">
              <p className="text-sm text-gray-600 font-medium">
                Top Male (Total)
              </p>
              <p className="text-lg font-bold text-blue-800">
                {computed.topMale.ageGroup}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {fmt(computed.topMale.maleTotal)}
              </p>
            </div>

            <div className="p-4 bg-green-100 rounded-xl shadow">
              <p className="text-sm text-gray-600 font-medium">
                Top Overall (Total)
              </p>
              <p className="text-lg font-bold text-green-800">
                {computed.topOverall.ageGroup}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {fmt(computed.topOverall.total)}
              </p>
            </div>

            {/* <div className="p-4 bg-yellow-100 rounded-xl shadow">
              <p className="text-sm text-gray-600 font-medium">Top in 2024</p>
              <p className="text-lg font-bold text-yellow-800">
                {computed.top2024.ageGroup}
              </p>
              <p className="text-xs text-gray-600 mt-1">
                {fmt(computed.top2024.total2024)}
              </p>
            </div> */}
          </div>
        )}

        {/* Table + Chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <Table
                striped
                bordered
                hover
                responsive
                className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden"
              >
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left text-sm">Age Group</th>
                    {years.map((y) => (
                      <React.Fragment key={y}>
                        <th className="p-2 text-sm">Female-{y}</th>
                        <th className="p-2 text-sm">Male-{y}</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cleaned.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="p-2 text-left text-sm">
                        {row["Age Group"]}
                      </td>
                      {years.map((y) => (
                        <React.Fragment key={`${idx}-${y}`}>
                          <td className="p-2 text-sm">
                            {fmt(row[`Female-${y}`])}
                          </td>
                          <td className="p-2 text-sm">
                            {fmt(row[`Male-${y}`])}
                          </td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Tip: Scroll horizontally on mobile to view all years.
            </p>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[520px] w-full h-96 p-4">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="PDF Icon" />
        </button>
      </div>
    </>
  );
};

export default AeroTable10;
